import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  LinearProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import {
  PeopleAlt as PeopleAltIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  TrendingUp as TrendingUpIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getLaborUtilizationSummary } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';

// ─── Constants ────────────────────────────────────────────────────────────────

const LABOR_COST_IDS = new Set([1, 2, 3, 4]);

const PERIOD_OPTIONS = [
  { value: 'week',      label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd',       label: 'Month to Date' },
  { value: 'ytd',       label: 'Year to Date' },
  { value: 'fy',        label: 'FY26 YTD' },
  { value: 'custom',    label: 'Custom Range' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtHours(n) {
  if (n == null || n === '') return '—';
  const v = parseFloat(n);
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function fmtPct(n) {
  if (n == null) return '—';
  return `${parseFloat(n).toFixed(1)}%`;
}

function utilizationColor(pct) {
  if (pct >= 70) return '#2E7D32';
  if (pct >= 50) return '#E65100';
  return '#C62828';
}

function utilizationBg(pct) {
  if (pct >= 70) return '#e8f5e9';
  if (pct >= 50) return '#fff3e0';
  return '#ffebee';
}

function mondayOf(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}

function shiftWeek(mondayStr, delta) {
  const d = new Date(mondayStr + 'T12:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ─── SummaryCard (matches Warehouse.js pattern) ───────────────────────────────

const SummaryCard = ({ title, value, subtitle, icon, color, onClick, active }) => (
  <Card
    elevation={active ? 6 : 2}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      outline: active ? `2px solid ${color}` : '2px solid transparent',
      outlineOffset: 1,
      transition: 'outline 0.15s, box-shadow 0.15s',
      '&:hover': onClick ? { boxShadow: 6, outline: `2px solid ${color}` } : {},
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: active ? `${color}30` : `${color}15`, color }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

// ─── UtilChip ─────────────────────────────────────────────────────────────────

function UtilChip({ pct }) {
  if (pct == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const color = utilizationColor(pct);
  const bg    = utilizationBg(pct);
  return (
    <Box
      sx={{
        display: 'inline-block',
        px: 1, py: 0.25,
        borderRadius: 1,
        bgcolor: bg,
        color,
        fontWeight: 700,
        fontSize: '0.8rem',
        minWidth: 52,
        textAlign: 'center',
      }}
    >
      {fmtPct(pct)}
    </Box>
  );
}

// ─── CategoryCard (bottom breakdown) ─────────────────────────────────────────

function CategoryCard({ label, hours, pct, color }) {
  return (
    <Card elevation={1} sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ color: color || 'text.primary', mt: 0.25 }}>
          {fmtHours(hours)} hrs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pct != null ? `${parseFloat(pct).toFixed(1)}% of total` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── EmployeeRow ──────────────────────────────────────────────────────────────

function EmployeeRow({ emp, canSeeCost, colCount }) {
  const [open, setOpen] = useState(false);
  const hasTopJobs = emp.top_jobs && emp.top_jobs.length > 0;

  return (
    <>
      <TableRow
        hover
        onClick={() => hasTopJobs && setOpen(o => !o)}
        sx={{
          cursor: hasTopJobs ? 'pointer' : 'default',
          bgcolor: open ? '#e8f0fe' : undefined,
        }}
      >
        {/* Expand toggle */}
        <TableCell sx={{ py: 0.5, width: 32, pr: 0 }}>
          {hasTopJobs && (
            <IconButton size="small" tabIndex={-1}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>

        {/* Employee name */}
        <TableCell sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>{emp.name}</Typography>
        </TableCell>

        {/* Department */}
        <TableCell sx={{ py: 0.5 }}>
          <Chip
            label={emp.department || '—'}
            size="small"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </TableCell>

        {/* Hours columns */}
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.total_hours)}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.billable_job)}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.billable_service)}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.non_billable)}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.travel)}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Typography variant="body2">{fmtHours(emp.warranty)}</Typography>
        </TableCell>

        {/* Util % */}
        <TableCell align="right" sx={{ py: 0.5 }}>
          <UtilChip pct={emp.utilization_pct} />
        </TableCell>

        {/* Cost (conditionally shown) */}
        {canSeeCost && (
          <TableCell align="right" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              {emp.cost != null ? `$${parseFloat(emp.cost).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
            </Typography>
          </TableCell>
        )}
      </TableRow>

      {/* Expanded: top jobs */}
      {hasTopJobs && (
        <TableRow>
          <TableCell
            colSpan={colCount}
            sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.25, px: 6, bgcolor: '#f5f7fa' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  Top Billable Jobs
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {emp.top_jobs.map(j => (
                    <Box
                      key={j.job}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        bgcolor: '#e3ecf7', borderRadius: 1, px: 1, py: 0.4,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                        {j.job}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        — {fmtHours(j.hours)} hrs
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function LaborUtilization() {
  const { user } = useAuth();
  const canSeeCost = LABOR_COST_IDS.has(user?.id);

  // Period / date state
  const [period,      setPeriod]      = useState('fy');
  const [weekDate,    setWeekDate]    = useState(mondayOf(todayStr()));
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');

  // UI state
  const [search,           setSearch]           = useState('');
  const [sortBy,           setSortBy]           = useState('name');
  const [sortDir,          setSortDir]          = useState('asc');
  const [selectedCategory, setSelectedCategory] = useState(null); // field key or null

  const handleCategoryClick = (fieldKey) => {
    if (selectedCategory === fieldKey) {
      // Toggle off — restore default sort
      setSelectedCategory(null);
      setSortBy('name');
      setSortDir('asc');
    } else {
      setSelectedCategory(fieldKey);
      setSortBy(fieldKey);
      setSortDir('desc');
    }
  };

  // Data
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { period };
      if (period === 'week' || period === 'last_week') {
        params.week   = period === 'week' ? weekDate : shiftWeek(weekDate, -1);
        params.period = 'week';
      }
      if (period === 'custom') {
        params.start = customStart;
        params.end   = customEnd;
      }
      const res = await getLaborUtilizationSummary(params);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [period, weekDate, customStart, customEnd]);

  useEffect(() => {
    if (period === 'custom') {
      if (customStart && customEnd) loadData();
    } else {
      loadData();
    }
  }, [loadData, period, weekDate, customStart, customEnd]);

  // ── Week navigation ─────────────────────────────────────────────────────────

  const prevWeek   = () => setWeekDate(w => shiftWeek(w, -1));
  const nextWeek   = () => setWeekDate(w => shiftWeek(w, +1));
  const isThisWeek = weekDate === mondayOf(todayStr());
  const showWeekNav = period === 'week' || period === 'last_week';

  // ── Sorting ─────────────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  // ── Flatten department data → employee list ─────────────────────────────────

  const allEmployees = React.useMemo(() => {
    if (!data) return [];
    // Prefer flat by_employee array if available; fall back to flattening by_department
    if (data.by_employee && data.by_employee.length > 0) {
      return data.by_employee;
    }
    return (data.by_department || []).flatMap(dept =>
      (dept.employees || []).map(emp => ({
        ...emp,
        department: emp.department || dept.department,
      }))
    );
  }, [data]);

  // ── Filter + sort ───────────────────────────────────────────────────────────

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return allEmployees.filter(e => {
      if (q && !(e.name || '').toLowerCase().includes(q)) return false;
      if (selectedCategory && !(parseFloat(e[selectedCategory]) > 0)) return false;
      return true;
    });
  }, [allEmployees, search, selectedCategory]);

  const sorted = React.useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string'
        ? av.localeCompare(bv)
        : parseFloat(av) - parseFloat(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDir]);

  // ── Summary values ──────────────────────────────────────────────────────────

  const s = data?.summary || {};

  const utilColor =
    s.utilization_pct != null
      ? utilizationColor(s.utilization_pct)
      : '#1A5490';

  // Column count for the colspan in expanded rows
  const colCount = canSeeCost ? 11 : 10;

  // ── Category breakdown totals ───────────────────────────────────────────────

  const totalHrs = parseFloat(s.total_hours) || 0;
  const billableTotal = (parseFloat(s.billable_job) || 0) + (parseFloat(s.billable_service) || 0);
  const nonBillable   = parseFloat(s.non_billable) || 0;
  const travelEtc     = (parseFloat(s.travel) || 0) + (parseFloat(s.training) || 0) + (parseFloat(s.sales) || 0);
  const warranty      = parseFloat(s.warranty) || 0;
  const safePct       = (n) => totalHrs > 0 ? (n / totalHrs) * 100 : null;

  // ── Column definitions ──────────────────────────────────────────────────────

  const columns = [
    { key: 'name',             label: 'Employee',     align: 'left'  },
    { key: 'department',       label: 'Department',   align: 'left'  },
    { key: 'total_hours',      label: 'Total Hrs',    align: 'right' },
    { key: 'billable_job',     label: 'Billable Job', align: 'right' },
    { key: 'billable_service', label: 'Billable Svc', align: 'right' },
    { key: 'non_billable',     label: 'Non-Bill',     align: 'right' },
    { key: 'travel',           label: 'Travel',       align: 'right' },
    { key: 'warranty',         label: 'Warranty',     align: 'right' },
    { key: 'utilization_pct',  label: 'Util %',       align: 'right' },
    ...(canSeeCost ? [{ key: 'cost', label: 'Cost', align: 'right' }] : []),
  ];

  // ────────────────────────────────────────────────────────────────────────────

  return (
    <Box>

      {/* ── Page Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Labor Utilization</Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          {data?.period?.label || 'Employee time tracking and billable hour analysis'}
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Summary Cards ── */}
      {data && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Total Hours"
              value={fmtHours(s.total_hours)}
              subtitle={`${allEmployees.length} employees`}
              icon={<AccessTimeIcon />}
              color="#1A5490"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Available Hours"
              value={fmtHours(s.available_hours)}
              subtitle={s.pto ? `${fmtHours(s.pto)} PTO excluded` : 'PTO excluded'}
              icon={<EventAvailableIcon />}
              color="#2E7D32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Utilization %"
              value={fmtPct(s.utilization_pct)}
              subtitle="Billable ÷ Available"
              icon={<TrendingUpIcon />}
              color={utilColor}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Billable to Job"
              value={fmtHours(s.billable_job)}
              subtitle={selectedCategory === 'billable_job' ? 'Click to clear filter' : 'Click to filter'}
              icon={<WorkIcon />}
              color="#1565C0"
              onClick={() => handleCategoryClick('billable_job')}
              active={selectedCategory === 'billable_job'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Billable to Service"
              value={fmtHours(s.billable_service)}
              subtitle={selectedCategory === 'billable_service' ? 'Click to clear filter' : 'Click to filter'}
              icon={<BuildIcon />}
              color="#00695C"
              onClick={() => handleCategoryClick('billable_service')}
              active={selectedCategory === 'billable_service'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Non-Billable"
              value={fmtHours(s.non_billable)}
              subtitle={selectedCategory === 'non_billable' ? 'Click to clear filter' : 'Click to filter'}
              icon={<BlockIcon />}
              color="#37474F"
              onClick={() => handleCategoryClick('non_billable')}
              active={selectedCategory === 'non_billable'}
            />
          </Grid>
        </Grid>
      )}

      {/* ── Filters Row ── */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

          {/* Search */}
          <PageSearchBar
            placeholder="Search employee…"
            value={search}
            onChange={(v) => setSearch(v)}
          />

          {/* Period selector */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={e => setPeriod(e.target.value)}>
              {PERIOD_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Week navigation */}
          {showWeekNav && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={prevWeek}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 160, textAlign: 'center', fontWeight: 500 }}>
                {data?.period?.label || '…'}
              </Typography>
              <IconButton size="small" onClick={nextWeek} disabled={isThisWeek}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}

          {/* Custom date pickers */}
          {period === 'custom' && (
            <>
              <TextField
                size="small"
                label="Start"
                type="date"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                label="End"
                type="date"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          )}

          {/* Active category filter badge */}
          {selectedCategory && (
            <Chip
              label={`Filtered: ${selectedCategory.replace(/_/g, ' ')}`}
              size="small"
              onDelete={() => handleCategoryClick(selectedCategory)}
              color="primary"
              variant="outlined"
            />
          )}

          {/* Employee count */}
          {data && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {sorted.length === allEmployees.length
                ? `${allEmployees.length} employees`
                : `${sorted.length} of ${allEmployees.length} employees`}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ── Main Table ── */}
      {data && (
        <Paper elevation={2} sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {/* Expand column */}
                  <TableCell sx={{ fontWeight: 'bold', width: 32, py: 1 }} />

                  {columns.map(col => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{ fontWeight: 'bold', py: 1 }}
                    >
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sorted.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search ? 'No employees match your search' : 'No data for this period'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {sorted.map(emp => (
                  <EmployeeRow
                    key={emp.name}
                    emp={emp}
                    canSeeCost={canSeeCost}
                    colCount={colCount}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Category Breakdown ── */}
      {data && totalHrs > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Hours Breakdown
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <CategoryCard
              label="Billable"
              hours={billableTotal}
              pct={safePct(billableTotal)}
              color="#1565C0"
            />
            <CategoryCard
              label="Non-Billable"
              hours={nonBillable}
              pct={safePct(nonBillable)}
              color="#37474F"
            />
            <CategoryCard
              label="Travel / Training / Sales"
              hours={travelEtc}
              pct={safePct(travelEtc)}
              color="#E65100"
            />
            <CategoryCard
              label="Warranty"
              hours={warranty}
              pct={safePct(warranty)}
              color="#6A1B9A"
            />
          </Box>
        </Box>
      )}

      {/* ── Util legend ── */}
      {data && (
        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap', px: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Util %:</Typography>
          {[
            { label: '≥ 70% — On target',       color: '#2E7D32', bg: '#e8f5e9' },
            { label: '50–69% — Needs attention', color: '#E65100', bg: '#fff3e0' },
            { label: '< 50% — Below target',     color: '#C62828', bg: '#ffebee' },
          ].map(l => (
            <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: l.bg, border: `2px solid ${l.color}` }} />
              <Typography variant="caption" color="text.secondary">{l.label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Utilization = (Billable Job + Billable Service + Warranty) ÷ (Total Hours − PTO)
          </Typography>
        </Box>
      )}

    </Box>
  );
}
